import * as Sentry from "@sentry/react";

/**
 * Seed Manager - Comprehensive seed data management for Sentry error reporting
 *
 * Features:
 * - Stores complete seed data as JSON strings for full Sentry visibility
 * - Sanitizes sensitive data (passwords, tokens, etc.)
 * - Handles file downloads and WebSocket seed messages
 * - Provides test functions for validation
 * - Environment-aware (development vs production)
 */

// Store seed data reference (not the actual data for security)
let currentSeedData = null;
let seedMetadata = null;

/**
 * Store seed data securely for error reporting
 * @param {object} seedData - The actual seed JSON data
 * @param {object} options - Configuration options
 */
export const storeSeedData = (seedData, options = {}) => {
  if (!seedData) {
    console.warn('No seed data provided to storeSeedData');
    return;
  }

  // Store metadata
  seedMetadata = {
    storedAt: new Date().toISOString(),
    dataSize: JSON.stringify(seedData).length,
    dataKeys: Object.keys(seedData),
    dataTypes: Object.fromEntries(
      Object.entries(seedData).map(([key, value]) => [key, typeof value])
    ),
    sessionId: generateSessionId(),
    environment: process.env.NODE_ENV,
    ...options
  };

  // Store actual data only if in development or if explicitly allowed
  const shouldStoreFullData = options.forceStore ||
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_STORE_SEED_DATA === 'true';

  if (shouldStoreFullData) {
    // Store sanitized version (remove sensitive fields)
    currentSeedData = sanitizeSeedData(seedData);

    // Create JSON strings for full visibility in Sentry
    const fullJsonString = JSON.stringify(currentSeedData, null, 2);
    const summaryJsonString = JSON.stringify({
      metadata: seedMetadata,
      dataTypes: Object.fromEntries(
        Object.entries(currentSeedData).map(([key, value]) => [
          key,
          Array.isArray(value) ? `Array(${value.length})` : typeof value
        ])
      ),
      dataKeys: Object.keys(currentSeedData),
      totalSize: fullJsonString.length
    }, null, 2);

    // Set Sentry context with stringified data for full visibility
    Sentry.setContext('seedData', {
      // Complete JSON string - this will be fully visible in Sentry
      fullSeedDataJSON: fullJsonString,

      // Summary as JSON string
      summaryJSON: summaryJsonString,

      // Metadata for quick reference
      metadata: seedMetadata,

      // Quick stats
      stats: {
        totalKeys: Object.keys(currentSeedData).length,
        jsonSize: fullJsonString.length,
        dataTypesCount: Object.keys(currentSeedData).length
      },

      warning: 'This contains actual seed data as JSON strings - fully viewable'
    });
  } else {
    // Production: Store only metadata and structure info
    currentSeedData = null;

    Sentry.setContext('seedData', {
      metadata: seedMetadata,
      dataStructure: getDataStructure(seedData),
      note: 'Actual seed data not stored in production for security'
    });
  }

  // Add breadcrumb
  Sentry.addBreadcrumb({
    message: 'Seed data stored for session',
    category: 'seed',
    level: 'info',
    data: {
      dataKeys: seedMetadata.dataKeys,
      dataSize: seedMetadata.dataSize,
      sessionId: seedMetadata.sessionId
    }
  });

  // Set session tags
  Sentry.setTags({
    hasSeedData: true,
    seedSessionId: seedMetadata.sessionId,
    seedDataSize: seedMetadata.dataSize
  });

  
};

/**
 * Sanitize seed data by removing or masking sensitive information
 */
const sanitizeSeedData = (data) => {
  if (!data || typeof data !== 'object') return data;

  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth',
    'email', 'phone', 'ssn', 'credit_card',
    'api_key', 'private_key', 'secret_key'
  ];

  const sanitized = JSON.parse(JSON.stringify(data)); // Deep clone

  const sanitizeObject = (obj) => {
    for (const key in obj) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        obj[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        sanitizeObject(obj[key]);
      }
    }
  };

  sanitizeObject(sanitized);
  return sanitized;
};


/**
 * Get data structure without actual values
 */
const getDataStructure = (data) => {
  if (!data || typeof data !== 'object') return typeof data;

  const getStructure = (obj, depth = 0) => {
    if (depth > 3) return '[Deep Object]'; // Prevent infinite recursion

    if (Array.isArray(obj)) {
      return obj.length > 0 ? `[Array of ${getStructure(obj[0], depth + 1)}]` : '[]';
    }

    if (typeof obj === 'object' && obj !== null) {
      const structure = {};
      for (const key in obj) {
        structure[key] = getStructure(obj[key], depth + 1);
      }
      return structure;
    }

    return typeof obj;
  };

  return getStructure(data);
};

/**
 * Generate a unique session ID
 */
const generateSessionId = () => {
  return `seed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current seed data (for debugging)
 */
export const getCurrentSeedData = () => ({
  data: currentSeedData,
  metadata: seedMetadata
});

/**
 * Clear stored seed data
 */
export const clearSeedData = () => {
  currentSeedData = null;
  seedMetadata = null;

  Sentry.setContext('seedData', null);
  Sentry.setTags({
    hasSeedData: false,
    seedSessionId: undefined,
    seedDataSize: undefined
  });
};

/**
 * Enhanced error reporting with seed context
 */
export const reportErrorWithSeedContext = (error, additionalContext = {}) => {
  const context = {
    ...additionalContext,
    seedMetadata,
    hasSeedData: !!currentSeedData,
    seedDataAvailable: !!currentSeedData
  };

  Sentry.captureException(error, {
    tags: {
      hasSeedData: !!currentSeedData,
      seedRelated: !!seedMetadata,
      ...additionalContext.tags
    },
    extra: {
      seedContext: context,
      // Include full seed data only if it exists and we're in development
      ...(currentSeedData && process.env.NODE_ENV === 'development' && {
        fullSeedData: currentSeedData
      }),
      ...additionalContext.extra
    }
  });
};

/**
 * Handle seed data from file downloads
 */
export const handleDownloadedSeedFile = async (fileUrl, options = {}) => {
  try {
   

    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Failed to download seed file: ${response.status}`);
    }

    const seedData = await response.json();

    // Store the downloaded seed data
    storeSeedData(seedData, {
      source: 'file-download',
      fileUrl: fileUrl,
      downloadedAt: new Date().toISOString(),
      ...options
    });

    
    return seedData;

  } catch (error) {
    console.error('Failed to download/parse seed file:', error);
    reportErrorWithSeedContext(error, {
      tags: { seedOperation: 'file-download' },
      extra: { fileUrl, ...options }
    });
    throw error;
  }
};



/**
 * Utility to wrap seed processing functions
 */
export const withSeedErrorHandling = (fn, operationName) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      // Report error with full seed context
      reportErrorWithSeedContext(error, {
        tags: { seedOperation: operationName },
        extra: { operationArgs: args, operationName }
      });
      throw error;
    }
  };
};
